<template>
  <div>
    <a-modal
      v-model="isShow"
      centered
      :width="1000"
      :mask-closable="false"
      :ok-button-props="{ props: { loading: submitting } }"
      title="生成 Profile"
      @ok="handleSubmit"
    >
      <a-row>
        <a-form
          class="simiot-compact-form"
          :form="form"
          :label-col="{ span: 9 }"
          :wrapper-col="{ span: 14 }"
          @submit="handleSubmit"
        >
          <a-col :span="11">
            <a-form-item :wrapper-col="{ span: 24 }">
              <a-textarea
                :rows="32"
                :placeholder="iccidPlaceholder"
                :style="{ color: iccidValidateStatus === 'error' ? 'red' : '' }"
                v-model.trim="iccidInput"
                @blur="analyzeIccidInput"
              />
            </a-form-item>
            <a-form-item label="ICCID总数" :style="{ display: 'inline-block', width: '50%' }">
              <span>{{ iccidsCount }}</span>
            </a-form-item>
            <a-form-item label="重复数量" :style="{ display: 'inline-block', width: '50%' }">
              <span>{{ duplicateIccidsCount }}</span>
            </a-form-item>
            <div v-if="iccidErrorMsg" style="color: red">{{ iccidErrorMsg }}</div>
          </a-col>

          <a-col :span="13">
            <a-form-item label="SM-DP+平台">
              <a-select
                v-decorator="['smdp_platform_id', { rules: [{ required: true, message: '请选择SM-DP+平台' }] }]"
                show-search
                option-filter-prop="children"
                :loading="loadingPlatformOptions"
              >
                <a-select-option v-for="opt in platformOptions" :key="opt.id" :value="opt.id">
                  {{ opt.name }}
                </a-select-option>
              </a-select>
            </a-form-item>

            <a-form-item label="SGP协议规范">
              <a-select
                v-decorator="['sgp_spec_id', { rules: [{ required: true, message: '请选择SGP协议规范' }] }]"
                show-search
                option-filter-prop="children"
                :loading="loadingSgpSpecOptions"
              >
                <a-select-option v-for="opt in sgpSpecOptions" :key="opt.id" :value="opt.id">
                  {{ opt.name }}
                </a-select-option>
              </a-select>
            </a-form-item>

            <a-form-item label="PRP重复下载规则">
              <a-select
                v-decorator="['prp_id', {
                  initialValue: 5,
                  rules: [{ required: true, message: '请选择PRP规则' }]
                }]"
              >
                <a-select-option :value="1">1 - 全态开放（任意EID）</a-select-option>
                <a-select-option :value="2">2 - 删除/就绪态开放（任意EID）</a-select-option>
                <a-select-option :value="3">3 - 全态绑定（同一EID）</a-select-option>
                <a-select-option :value="4">4 - 删除/就绪态绑定（同一EID）</a-select-option>
                <a-select-option :value="5">5 - 错误敏感模式（同一EID）</a-select-option>
              </a-select>
            </a-form-item>

            <a-form-item label="备注">
              <a-textarea
                v-decorator="['remark', { rules: [{ max: 200, message: '最多200个字符' }] }]"
                :auto-size="{ minRows: 3, maxRows: 5 }"
              />
            </a-form-item>
          </a-col>
        </a-form>
      </a-row>
    </a-modal>
  </div>
</template>

<script>
import { findSmdpPlatforms, generateProfilesFromProfileDatas } from '@/api/profile_data'
import { findCommonTypeOptions } from '@/api/common_type'
import { analyzeIccids, maxIccidLines } from '@/utils/iccid'

export default {
  name: 'GenerateProfilesFromProfileDatas',
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    iccids: {
      type: Array,
      default() {
        return []
      }
    }
  },
  data() {
    return {
      form: this.$form.createForm(this, { name: 'generate_profiles_from_profile_datas' }),
      submitting: false,
      iccidInput: (this.iccids || []).join('\n'),
      parsedIccids: [],
      iccidsCount: 0,
      duplicateIccidsCount: 0,
      iccidValidateStatus: '',
      iccidErrorMsg: '',
      iccidPlaceholder: '可以直接复制excel中整列ICCID；\n' +
        '手动输入多个ICCID，一行一个。\n' +
        '仅处理未使用的 ProfileData。\n' +
        '生成入库后供应商与 ProfileData 导入时一致。',
      platformOptions: [],
      loadingPlatformOptions: false,
      sgpSpecOptions: [],
      loadingSgpSpecOptions: false
    }
  },
  computed: {
    isShow: {
      get() { return this.visible },
      set(val) { this.$emit('update:visible', val) }
    }
  },
  created() {
    this.fetchPlatformOptions()
    this.fetchSgpSpecOptions()
    if (this.iccidInput) {
      this.analyzeIccidInput()
    }
  },
  methods: {
    fetchPlatformOptions() {
      this.loadingPlatformOptions = true
      findSmdpPlatforms().then((res) => {
        if (res.code === 0) this.platformOptions = res.data || []
        this.loadingPlatformOptions = false
      }).catch(() => { this.loadingPlatformOptions = false })
    },
    fetchSgpSpecOptions() {
      this.loadingSgpSpecOptions = true
      findCommonTypeOptions('product_esim_sgp_spec').then((res) => {
        if (res.code === 0) this.sgpSpecOptions = res.data || []
        this.loadingSgpSpecOptions = false
      }).catch(() => { this.loadingSgpSpecOptions = false })
    },
    analyzeIccidInput() {
      if (!this.iccidInput) {
        this.parsedIccids = []
        this.iccidsCount = 0
        this.duplicateIccidsCount = 0
        this.iccidValidateStatus = 'error'
        this.iccidErrorMsg = '请输入 ICCID'
        return
      }

      const { lines, validIccids, errorText } = analyzeIccids(this.iccidInput)
      if (lines > maxIccidLines) {
        this.parsedIccids = []
        this.iccidsCount = 0
        this.duplicateIccidsCount = 0
        this.iccidValidateStatus = 'error'
        this.iccidErrorMsg = `ICCID数量不能超过${maxIccidLines}`
        return
      }

      this.iccidInput = errorText + validIccids.join('\n')
      this.parsedIccids = this.$lodash.uniq(validIccids)
      this.iccidsCount = validIccids.length
      this.duplicateIccidsCount = validIccids.length - this.parsedIccids.length

      if (errorText) {
        this.iccidValidateStatus = 'error'
        this.iccidErrorMsg = '号码包含错误，无法提交'
      } else if (this.parsedIccids.length === 0) {
        this.iccidValidateStatus = 'error'
        this.iccidErrorMsg = '请输入 ICCID'
      } else if (this.duplicateIccidsCount > 0) {
        this.iccidValidateStatus = 'error'
        this.iccidErrorMsg = '录入的 ICCID 存在重复'
      } else {
        this.iccidValidateStatus = 'success'
        this.iccidErrorMsg = ''
      }
    },
    handleSubmit(e) {
      e.preventDefault()
      if (this.submitting) {
        this.$warning({ title: '请勿重复提交', content: '' })
        return
      }
      this.analyzeIccidInput()
      if (this.iccidValidateStatus === 'error') {
        return
      }
      this.form.validateFields((err, values) => {
        if (err) return
        this.submitting = true
        generateProfilesFromProfileDatas({
          ...values,
          iccids: this.parsedIccids
        }).then((res) => {
          if (res.code === 0) {
            this.isShow = false
            this.$emit('completed')
            this.$success({
              title: '提交成功',
              content: (<div><p>操作成功，具体结果请至批次操作记录查看</p></div>)
            })
          }
          this.submitting = false
        }).catch(() => { this.submitting = false })
      })
    }
  }
}
</script>
